// Phase 12 FIX 3 — Single comprehensive guided tour with always-visible Cancel.
// One master tour spanning the whole platform (~22 steps).
// State persists in localStorage; never re-auto-starts after dismiss/complete.

import { useCallback, useEffect, useMemo, useRef, useState, createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Joyride, STATUS, EVENTS, ACTIONS } from "react-joyride";
import { X } from "lucide-react";
import {
    buildTourSteps, markTourCompleted, markTourDismissed, readTourState, resetTour,
} from "../lib/guidedTour";

const API = process.env.REACT_APP_BACKEND_URL;

const TourCtx = createContext(null);
export function useTour() { return useContext(TourCtx); }

/** Pull a known seed-example analysis id from the backend so we can route the
 * mid-tour to a real dashboard, not the empty list. */
async function fetchSeedExampleId() {
    try {
        const r = await fetch(`${API}/api/analyses?limit=1`, { credentials: "include" });
        if (!r.ok) return null;
        const data = await r.json();
        const item = (data.items || [])[0];
        return item?.id || null;
    } catch {
        return null;
    }
}

/** Wait up to `timeoutMs` for `selector` to appear in the DOM. Returns the element or null. */
async function waitForSelector(selector, timeoutMs = 2500) {
    if (!selector || selector === "body") return document.body;
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
        const el = document.querySelector(selector);
        if (el) return el;
        await new Promise((r) => setTimeout(r, 80));
    }
    return null;
}

export function TourProvider({ children, user }) {
    const navigate = useNavigate();
    const [running, setRunning] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const [steps, setSteps] = useState(() => buildTourSteps());
    const [paused, setPaused] = useState(false);
    const exampleIdRef = useRef(null);
    const startingRef = useRef(false);

    const start = useCallback(async () => {
        if (startingRef.current) return;
        startingRef.current = true;
        // Resolve a seed-example id so mid-tour dashboard steps point at real data.
        if (!exampleIdRef.current) {
            exampleIdRef.current = await fetchSeedExampleId();
        }
        const built = buildTourSteps({ exampleAnalysisId: exampleIdRef.current });
        // Phase 12.11 — Failure A: filter out any step whose `target` isn't a
        // non-empty string, AND ensure every step has an explicit `placement`.
        // Joyride v3 internals (Floating UI middleware) call
        // `placement.startsWith(...)` on every step — an undefined placement
        // crashes the whole render with TypeError on the third transition.
        const jsteps = built
            .map((s, idx) => {
                let step = { ...s };
                if (typeof step.target !== "string" || !step.target) {
                    console.warn(`[tour] step ${idx} ('${s.id}') has invalid target, coercing to body:`, s.target);
                    step.target = "body";
                    step.placement = "center";
                }
                if (typeof step.placement !== "string" || !step.placement) {
                    step.placement = step.target === "body" ? "center" : "bottom";
                }
                return step;
            })
            .map((s) => ({
                target: s.target,
                placement: s.placement,
                title: s.title,
                content: s.content,
            }));
        setSteps(jsteps);
        setStepIndex(0);
        // Navigate to the first step's route if defined.
        if (built[0]?.route && typeof window !== "undefined" &&
            window.location.pathname !== built[0].route.split("#")[0]) {
            navigate(built[0].route);
        }
        // Small delay so the destination has mounted.
        setTimeout(() => {
            setRunning(true);
            startingRef.current = false;
        }, 350);
    }, [navigate]);

    const cancel = useCallback((reason = "user_cancelled") => {
        setRunning(false);
        setPaused(false);
        markTourDismissed();
        // Re-show via user menu → resetTour() + start()
    }, []);

    const finish = useCallback(() => {
        setRunning(false);
        markTourCompleted();
    }, []);

    const restart = useCallback(async () => {
        resetTour();
        setStepIndex(0);
        await start();
    }, [start]);

    // First-time auto-start (after 600ms) on the home page, only if not dismissed/completed.
    useEffect(() => {
        const state = readTourState();
        const everSeen = state.dismissed_at || state.completed_at;
        if (everSeen) return;
        if (typeof window === "undefined") return;
        if (window.location.pathname !== "/") return;
        const t = setTimeout(() => { start(); }, 600);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Esc cancels the tour at any step.
    useEffect(() => {
        if (!running) return;
        const onKey = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                cancel("escape");
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [running, cancel]);

    // Pause tour if the user manually navigates away from a step's route.
    // We don't auto-resume — show a toast (handled below by FloatingCancelPill).
    useEffect(() => {
        if (!running) return;
        const expected = buildTourSteps({ exampleAnalysisId: exampleIdRef.current })[stepIndex]?.route;
        if (!expected) return;
        const path = window.location.pathname;
        const expectedPath = expected.split("#")[0];
        if (path !== expectedPath) {
            setPaused(true);
        } else if (paused) {
            setPaused(false);
        }
    }, [running, stepIndex, paused]);

    // Joyride callback — pre-navigate routes when transitioning between steps.
    // Phase 12.4 — uncontrolled stepIndex mode. Joyride drives its own state;
    // we only intercept callbacks to (a) navigate to the right route BEFORE
    // joyride tries to render the next step, (b) skip steps whose target is
    // missing, (c) finalize cancel/complete persistence.
    const handleJoyride = useCallback(async (data) => {
        const { status, type, index, action } = data;

        if (status === STATUS.FINISHED) {
            finish();
            return;
        }
        if (status === STATUS.SKIPPED || action === ACTIONS.CLOSE) {
            cancel("close");
            return;
        }

        // On STEP_AFTER, joyride will internally advance to (index + direction).
        // Pre-navigate to that step's route so its target exists when joyride renders.
        if (type === EVENTS.STEP_AFTER) {
            const built = buildTourSteps({ exampleAnalysisId: exampleIdRef.current });
            const direction = action === ACTIONS.PREV ? -1 : 1;
            const nextIndex = index + direction;
            if (nextIndex < 0 || nextIndex >= built.length) {
                // Joyride will finalize via FINISHED status; nothing to do.
                return;
            }
            const next = built[nextIndex];
            if (next?.route) {
                const expectedPath = next.route.split("#")[0];
                if (window.location.pathname !== expectedPath) {
                    navigate(next.route);
                }
            }
            return;
        }

        // If joyride can't find a step's target, log it (don't crash).
        if (type === EVENTS.TARGET_NOT_FOUND) {
            console.warn(`[tour] step ${index} target missing — joyride will skip`);
        }
    }, [navigate, finish, cancel]);

    const value = useMemo(
        () => ({ start, cancel, restart, finish, running, stepIndex, paused }),
        [start, cancel, restart, finish, running, stepIndex, paused],
    );

    return (
        <TourCtx.Provider value={value}>
            {children}
            {running && (
                <>
                    <Joyride
                        run={running && !paused}
                        steps={steps}
                        continuous
                        showSkipButton
                        disableBeacon
                        scrollToFirstStep
                        onEvent={handleJoyride}
                        tooltipComponent={CustomTooltip}
                        styles={{
                            options: {
                                primaryColor: "#1A237E",
                                textColor: "#1A1A1A",
                                backgroundColor: "#FCFAF7",
                                arrowColor: "#FCFAF7",
                                overlayColor: "rgba(26, 26, 26, 0.45)",
                                zIndex: 10000,
                                width: 380,
                            },
                        }}
                        locale={{
                            next: "Next →",
                            back: "← Back",
                            last: "Done",
                            skip: "× Cancel tour",
                        }}
                    />
                    <FloatingCancelPill onCancel={() => cancel("pill")} paused={paused} onResume={() => setPaused(false)} />
                </>
            )}
        </TourCtx.Provider>
    );
}

/** Custom tooltip — adds an always-visible Cancel tour button in the top-right. */
function CustomTooltip(props) {
    const {
        backProps, primaryProps, skipProps, tooltipProps,
        step, index, size, isLastStep,
    } = props;
    // Note: skipProps is hidden by buttonSkip:display:none but we still have its onClick.

    return (
        <div
            {...tooltipProps}
            data-testid="tour-tooltip"
            className="relative bg-paper border border-ink/15 shadow-xl"
            style={{ maxWidth: 380, borderRadius: 8, padding: "20px 20px 16px" }}
        >
            <button
                type="button"
                onClick={skipProps?.onClick}
                data-testid="tour-cancel-button"
                aria-label="Cancel tour"
                title="Cancel tour"
                className="absolute top-2 right-2 inline-flex items-center gap-1 px-1.5 py-1 rounded hover:bg-paper-soft text-ink-soft hover:text-ink transition-colors"
            >
                <X size={13} />
                <span className="font-mono text-[10px] uppercase tracking-[0.14em]">Cancel tour</span>
            </button>

            <div className="pr-20">
                {step.title && (
                    <div className="font-serif font-semibold text-[14px] text-ink mb-1.5">
                        {step.title}
                    </div>
                )}
                <div
                    className="font-serif text-[13px] leading-[1.6] text-ink"
                    data-testid="tour-tooltip-content"
                >
                    {step.content}
                </div>
            </div>

            <div className="mt-4 pt-3 border-t border-ink/8 flex items-center justify-between gap-3">
                <div className="font-mono text-[10px] tracking-[0.14em] text-ink-soft">
                    Step {index + 1} of {size}
                </div>
                <div className="flex items-center gap-2">
                    {index > 0 && (
                        <button
                            {...backProps}
                            data-testid="tour-back"
                            className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-soft hover:text-ink px-2 py-1"
                        >
                            ← Back
                        </button>
                    )}
                    <button
                        {...primaryProps}
                        data-testid="tour-next"
                        className="font-mono text-[11px] uppercase tracking-[0.14em] bg-ink text-paper hover:bg-amber-accent transition-colors px-3 py-2"
                    >
                        {isLastStep ? "Done" : "Next →"}
                    </button>
                </div>
            </div>

            <button
                type="button"
                onClick={skipProps?.onClick}
                data-testid="tour-skip-to-end"
                className="absolute bottom-2 left-3 font-mono text-[10px] text-ink-soft hover:text-ink underline underline-offset-2"
            >
                Skip to end
            </button>
        </div>
    );
}

/** Persistent floating Cancel pill at the bottom-right of the viewport. */
function FloatingCancelPill({ onCancel, paused, onResume }) {
    return (
        <div
            className="fixed bottom-6 right-6 z-[6000] flex items-center gap-2"
            data-testid="tour-floating-pill"
        >
            {paused && (
                <button
                    onClick={onResume}
                    className="bg-paper border border-amber-accent text-ink font-mono text-[11px] uppercase tracking-[0.14em] px-3 py-2 hover:bg-amber-soft transition-colors"
                    data-testid="tour-resume"
                >
                    Resume tour
                </button>
            )}
            <button
                onClick={onCancel}
                className="bg-ink text-paper font-mono text-[11px] uppercase tracking-[0.14em] px-3 py-2 hover:bg-red-700 transition-colors flex items-center gap-1.5"
                data-testid="tour-floating-cancel"
            >
                <X size={12} />
                Cancel tour
            </button>
        </div>
    );
}

/** First-time card shown on the analyses list when no tour has been completed. */
export function FirstTimeTourCard() {
    const ctx = useTour();
    const state = readTourState();
    const dismissed = state.dismissed_at;
    const completed = state.completed_at;
    const [hidden, setHidden] = useState(false);
    if (hidden || dismissed || completed) return null;
    return (
        <div
            data-testid="first-time-tour-card"
            className="border border-amber-accent/40 bg-amber-soft/40 px-5 py-3 mb-6 flex items-center justify-between gap-4"
        >
            <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-amber-accent mb-0.5">
                    First time here?
                </div>
                <p className="text-[13px] text-ink leading-snug">
                    Take a 90-second tour of the entire platform — you can stop or restart any time.
                </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
                <button
                    onClick={() => { setHidden(true); markTourDismissed(); }}
                    className="text-[11px] text-ink-soft hover:text-ink font-mono"
                    data-testid="first-time-tour-later"
                >
                    Later
                </button>
                <button
                    onClick={() => ctx?.start?.()}
                    data-testid="first-time-tour-start"
                    className="px-3 py-1.5 bg-ink text-paper text-[11px] font-mono uppercase tracking-[0.14em] hover:bg-amber-accent transition-colors"
                >
                    Take the tour →
                </button>
            </div>
        </div>
    );
}

// Backwards-compat exports for older imports.
export const TOURS = {
    platform_overview: { id: "platform_overview", name: "Platform overview", durationLabel: "~90s" },
};
